import {
  ApiCustomer,
  ApiOutlet,
  ApiRegister,
  ApiRetailer,
  ApiUser,
} from '@vend/types'
import moment from 'moment'
import { env, ENVS } from '../../../../lib/env'
import {
  PaymentRequestDto,
  LineItem as PaymentRequestLineItem,
  RetailerDetails,
} from '../../models/invoicing-payment-request'
import { LineItem, LineItemTaxComponent } from '../../models/line-item'
import { Finals } from '../../models/sale'
import { SaleDiscount } from '../../models/sale-discount'

export const INTEGRATION_KEY = 'x-series'

/**
 * Format a date string to "MMMM D, YYYY" format (e.g., "September 2, 2025")
 */
export const formatDate = (dateString?: string): string => {
  if (!dateString) return ''
  return moment(dateString).format('MMMM D, YYYY')
}

/**
 * Transform Redux line items to PaymentRequestDto LineItem format
 */
export const transformLineItems = (
  items: LineItem[]
): PaymentRequestLineItem[] => {
  return items.map((lineItem: LineItem) => ({
    totalAmount: Math.round(Number(lineItem.totals?.inclusivePrice || 0) * 100),
    subtotal: Math.round(Number(lineItem.totals?.exclusivePrice || 0) * 100),
    discountTotal: Math.round(Number(lineItem.displayDiscount || 0) * 100),
    feeTotal: 0, // TODO handle fees
    taxTotal: Math.round(Number(lineItem.totals?.tax || 0) * 100),
    lineItemRef: lineItem.id || lineItem.product?.id, // lineItem.id is undefined while the sale is not saved
    quantity: Number(lineItem.quantity || 0),
    unitAmount: Math.round(Number(lineItem.basePrice || 0) * 100), // lineItem.price = base unit price including line item discounts; lineItem.basePrice = base unit price excluding discounts
    name: lineItem.product?.name || '',
    description: lineItem.note || undefined,
    taxes:
      lineItem.taxComponents?.map((tax: LineItemTaxComponent) => ({
        taxRef: tax.rate_id,
        type: tax.rate?.name,
        rate: Number(tax.rate?.rate || 0),
        totalAmount: Math.round(Number(tax.total_tax || 0) * 100),
      })) || [],
    discounts: lineItem.displayDiscount
      ? [
          {
            discountRef: `line-discount-${lineItem.id || lineItem.product?.id}`, // TODO confirm ref
            type: 'line-item-discount', // TODO confirm type
            unitAmount: Math.round(Number(lineItem.displayDiscount || 0) * 100),
            totalAmount: Math.round(
              Number(lineItem.displayDiscount || 0) * 100
            ),
          },
        ]
      : [],
    fees: [], // TODO handle fees
  }))
}

interface BuildPaymentRequestDtoParams {
  customer: ApiCustomer | null
  lineItems: LineItem[]
  saleIdFromStore: string | null | undefined
  saleFinals: Finals
  currentOutlet: ApiOutlet
  currentRegister: ApiRegister
  retailer: ApiRetailer
  saleNote?: string
  discount: SaleDiscount | null | undefined
  amountTendered: string
  user: ApiUser
  lspayPaymentTypeId: string | undefined
}

/**
 * Build a complete PaymentRequestDto from sale data
 */
export const buildPaymentRequestDto = ({
  customer,
  lineItems,
  saleIdFromStore,
  saleFinals,
  currentOutlet,
  currentRegister,
  retailer,
  discount,
  amountTendered,
  user,
  lspayPaymentTypeId,
}: BuildPaymentRequestDtoParams): PaymentRequestDto => {
  const transformedLineItems = transformLineItems(lineItems)

  return {
    requestedAmount: Math.round(Number(amountTendered || 0) * 100),
    sendToEmail: customer?.email || '',
    integrationKey: INTEGRATION_KEY,
    location: {
      locationRef: currentOutlet?.id || '',
      name: currentOutlet?.name || '',
      email: currentOutlet?.email || '',
    },
    customer: {
      customerRef: customer?.id || '',
      firstName: customer?.first_name || '',
      lastName: customer?.last_name || '',
      email: customer?.email || '',
    },
    note: '',
    documentRefs: saleIdFromStore ? [saleIdFromStore] : [],
    registerRef: currentRegister?.id || '',
    shopRef: currentOutlet?.id || '',
    accountRef: retailer?.id || '',
    employeeRef: user?.id || '',
    pricingTables: [
      {
        title: 'Sale',
        totalAmount: Math.round(Number(saleFinals.inclusivePrice || 0) * 100), // saleFinals.inclusivePrice = The total price including tax and discount
        subtotal: Math.round(Number(saleFinals.subtotal || 0) * 100), // saleFinals.exclusivePrice = The total price excluding tax and including discount; saleFinals.subtotal = The display subtotal. Calculated differently for inclusive vs exclusive retailers.
        discountTotal: Math.round(Number(discount?.price || 0) * 100), // discount value is calculated differently based on inclusive vs exclusive retailers; discount?.price contains value for tax exclusive retailers. For example on how to calculate for both cases see SaleDiscount.tsx
        feeTotal: 0, // TODO handle fees
        taxTotal: Math.round(Number(saleFinals.tax || 0) * 100), // saleFinals.tax = The total tax including discount.
        currency: currentOutlet?.currency,
        lineItems: transformedLineItems,
        taxes: Object.values(saleFinals.taxes || {}).map(tax => ({
          taxRef: tax.rate?.id || '',
          type: tax.rate?.name || '',
          rate: Math.round(Number(tax.rate?.rate || 0) * 100 * 1000) / 1000,
          totalAmount: Math.round(Number(tax.total || 0) * 100), // Convert to cents
        })),
        discounts: [],
        fees: [], // TODO handle fees
      },
    ],
    metadata:
      lspayPaymentTypeId && retailer?.id && currentRegister?.id
        ? {
            retailerId: retailer.id,
            registerId: currentRegister.id,
            retailerPaymentTypeId: lspayPaymentTypeId,
            documentRef: '', // Will be set after sale is saved
          }
        : undefined,
  }
}

/**
 * Get the preview URL based on the current environment and retailer status
 * Demo accounts (a subset of production) use staging URL
 */
const baseUrlMap: Record<string, string> = {
  [ENVS.DEVELOPMENT]: 'https://local.qmach.in',
  [ENVS.PRODUCTION]: 'https://invoicing.lightspeed.app',
}

const DEMO_STAGING_URL = 'https://invoicing-web-cpq.lightspeedstg.app'

export const getPreviewURL = (retailer?: ApiRetailer): string => {
  const currentEnv = env.get()

  // Demo accounts run in production but should use staging URL
  if (currentEnv === ENVS.PRODUCTION && retailer?.account_status === 'demo') {
    return `${DEMO_STAGING_URL}/p/proposals/preview`
  }

  const baseUrl = baseUrlMap[currentEnv]
  return `${baseUrl}/p/proposals/preview`
}

/**
 * Get the base URL for invoicing based on environment and account status
 */
const getInvoicingBaseUrl = (retailer?: ApiRetailer): string => {
  const currentEnv = env.get()

  // Demo accounts run in production but should use staging URL
  if (currentEnv === ENVS.PRODUCTION && retailer?.account_status === 'demo') {
    return DEMO_STAGING_URL
  }

  return baseUrlMap[currentEnv] || baseUrlMap[ENVS.PRODUCTION]
}

/**
 * Get the URL to view a payment request in the invoicing web app
 */
export const getPaymentRequestViewURL = (
  proposalHash: string,
  retailer?: ApiRetailer
): string => {
  const baseUrl = getInvoicingBaseUrl(retailer)
  return `${baseUrl}/p/proposals/${proposalHash}`
}

/**
 * Build RetailerDetails required for validating Invoicing account
 */
interface BuildRetailerDetails {
  retailer: ApiRetailer
}

export const buildRetailerDetails = ({
  retailer,
}: BuildRetailerDetails): RetailerDetails => {
  return {
    retailerId: retailer.id,
    shopName: retailer.name,
    currency: retailer.currency.code,
    language: retailer.culture,
    integrationKey: INTEGRATION_KEY,
  }
}
